import React, { useRef, useState, useEffect } from "react";
import { ArrowLeft, ShieldCheck, Zap } from "lucide-react";
import { Button } from "../../tailwind/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../tailwind/card";
import { BillingDetailsForm } from "./BillingDetailsForm";
import validateBillingDetails from "../../../utils/billingValidation";
import { waBtn } from "../../../pages/whatsapp/utils/whatsappUiTokens";

export const CheckoutSection = ({
  selectedPlanDetails,
  gstAmount,
  totalAmount,
  showZeroMarkup,
  isProcessing,
  isLoadingSubscription,
  isLoadingBillingDetails,
  isSavingBillingDetails,
  isProcessingPayment,
  subscription,
  billingDetails,
  onBack,
  onPay,
  onSaveBillingDetails,
  onChangePlan,
  allPlans = [],
  gstRate = 0,
  subscriptionStartDate,
  subscriptionEndDate,
}) => {
  const formRef = useRef(null);
  const [savePhase, setSavePhase] = useState('idle');
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (savePhase === 'dispatched' && isSavingBillingDetails) {
      setSavePhase('saving');
    }
    if (savePhase === 'saving' && !isSavingBillingDetails) {
      setSavePhase('idle');
      onPay();
    }
  }, [savePhase, isSavingBillingDetails, onPay]);

  const hasBillingDetailsChanged = (current, original) => {
    if (!original) {
      const hasAnyValues = Object.values(current).some(val =>
        typeof val === 'string' ? val.trim() !== '' : val
      ) || (current.address && Object.values(current.address).some(val =>
        typeof val === 'string' ? val.trim() !== '' : val
      ));
      return hasAnyValues;
    }

    const fields = ['legalName', 'email', 'phone', 'gstin'];
    for (const field of fields) {
      if ((current[field] || '').trim() !== (original?.[field] || '').trim()) {
        return true;
      }
    }

    const addressFields = ['line1', 'line2', 'city', 'state', 'pinCode', 'country'];
    for (const field of addressFields) {
      if ((current.address?.[field] || '').trim() !== (original?.address?.[field] || '').trim()) {
        return true;
      }
    }
    return false;
  };

  const handlePayClick = () => {
    if (!formRef.current) {
      onPay();
      return;
    }

    const currentValues = formRef.current.getCurrentValues();
    const fieldErrors = validateBillingDetails(currentValues || {});
    const hasFieldErrors = Object.keys(fieldErrors).length > 0;
    if (hasFieldErrors) {
      setFormErrors(fieldErrors);
      return;
    }
    setFormErrors({});

    const hasChanged = hasBillingDetailsChanged(currentValues, billingDetails);

    if (hasChanged) {
      setSavePhase('dispatched');
      onSaveBillingDetails(currentValues);
    } else {
      onPay();
    }
  };

  let annualDiscount = 0;
  if (selectedPlanDetails?.period === "year") {
    const monthlyPlan = allPlans.find(plan => plan.period === "month");
    if (monthlyPlan) {
      const yearlyEquivalent = monthlyPlan.totalPrice * 12;
      annualDiscount = yearlyEquivalent - selectedPlanDetails.totalPrice;
    }
  }
  return (
    <div className="animate-in slide-in-from-right-8 duration-300 pb-20">
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={onBack}
          className="gap-2 pl-0 hover:bg-transparent hover:text-[hsl(158,48%,32%)] dark:text-white dark:hover:text-[hsl(158,48%,32%)] hover:cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Complete your purchase</h2>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">1</div>
                <CardTitle className="text-lg">Business Details</CardTitle>
                {isLoadingBillingDetails && (
                  <div className="ml-auto text-xs text-blue-600 font-medium">Loading...</div>
                )}
              </div>
            </CardHeader>
            <CardContent>
                <BillingDetailsForm
                  ref={formRef}
                  initialBillingDetails={billingDetails}
                  errors={formErrors}
                />
            </CardContent>
          </Card>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl p-6">
            <h4 className="flex items-center gap-2 font-bold text-blue-900 dark:text-blue-100 mb-4">
              <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400 fill-blue-600 dark:fill-blue-400" /> What happens immediately after payment
            </h4>
            <ul className="space-y-3">
              {[
                "Account activates instantly — no manual step needed",
                "WhatsApp Send Message & Campaigns unlocked",
                "GST invoice sent to your email address",
                "Zero message markup active for 3 months",
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-sm text-blue-800 dark:text-blue-200">
                  <div className="h-5 w-5 rounded-full bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-[10px] flex-shrink-0">{i + 1}</div>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-gray-900 rounded-t-xl p-6 text-white">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Your Order</p>
            <h3 className="text-2xl font-bold">{selectedPlanDetails?.name} Plan</h3>
            {subscription && (
              <div className="mt-2 text-xs text-green-400 font-medium">
                ✓ Subscription {subscription.status.toLowerCase()}
              </div>
            )}
            <p className="text-sm text-gray-400 mt-1">
              {(subscriptionStartDate ? (subscriptionStartDate instanceof Date ? subscriptionStartDate : new Date(subscriptionStartDate)) : new Date()).toLocaleDateString("en-US", { 
                month: "short", 
                day: "numeric", 
                year: "numeric" 
              })} →
              {" "}
              {(subscriptionEndDate ? (subscriptionEndDate instanceof Date ? subscriptionEndDate : new Date(subscriptionEndDate)) : (() => {
                const d = new Date(subscriptionStartDate ? (subscriptionStartDate instanceof Date ? subscriptionStartDate : new Date(subscriptionStartDate)) : new Date());
                if (selectedPlanDetails?.period === "year") {
                  d.setFullYear(d.getFullYear() + 1);
                } else {
                  d.setMonth(d.getMonth() + 1);
                }
                return d;
              })()).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-t-0 rounded-b-xl p-6 shadow-sm">
            <div className="space-y-3 text-sm mb-6 pb-6 border-b border-gray-100 dark:border-gray-700">
              <div className="flex justify-between text-gray-900 dark:text-white font-medium">
                <span>Base subscription</span>
                <span>₹{selectedPlanDetails?.totalPrice.toLocaleString()}</span>
              </div>
              {selectedPlanDetails?.period === "year" && annualDiscount > 0 && (
                <div className="flex justify-between text-green-600 dark:text-green-400">
                  <span>Annual discount (10%)</span>
                  <span>-₹{annualDiscount.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span>GST ({(gstRate * 100).toFixed(0)}%)</span>
                <span>₹{gstAmount.toLocaleString()}</span>
              </div>
            </div>

            <div className="flex justify-between items-center mb-6">
              <span className="font-bold text-gray-900 dark:text-white">Total due today</span>
              <span className="text-2xl font-extrabold text-gray-900 dark:text-white">₹{totalAmount.toLocaleString()}</span>
            </div>

            <Button
              className={`w-full h-12 text-base ${waBtn.primary} hover:cursor-pointer shadow-md hover:shadow-lg transition-all`}
              onClick={handlePayClick}
              disabled={isProcessing || isLoadingSubscription || isSavingBillingDetails || savePhase !== 'idle' || isProcessingPayment}
            >
              {isProcessingPayment ? (
                "Processing Payment..."
              ) : isProcessing ? (
                "Processing Payment..."
              ) : isLoadingSubscription ? (
                "Creating Subscription..."
              ) : isSavingBillingDetails || savePhase !== 'idle' ? (
                "Saving Billing Details..."
              ) : (
                <span className="flex items-center gap-2">
                  Pay ₹{totalAmount.toLocaleString()} <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded font-normal">Razorpay</span>
                </span>
              )}
            </Button>

            <div className="mt-4 flex justify-center gap-3 text-[10px] text-gray-400">
              <span className="flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> SSL secured
              </span>
              <span>•</span>
              <span>PCI DSS</span>
              <span>•</span>
              <span>UPI · Cards · NetBanking</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
